import styled from '@emotion/styled';
import {
  faCaretDown,
  faCaretUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Cookies from 'js-cookie';

import Button from '~/common/components/Buttons/Button';
import Menu from '~/common/components/Menus/Menu';
import MenuButton from '~/common/components/Menus/MenuButton';
import MenuItem from '~/common/components/Menus/MenuItem';
import MenuList from '~/common/components/Menus/MenuList';
import { BOLT_IMPERSONATE_COOKIE, USER_ROLE } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import useUrl from '~/common/hooks/useUrl';
import { reloadPage } from '~/common/util/browserNavigation';

const StyledIcon = styled<any>(FontAwesomeIcon)`
  margin-left: 8px;
  ${({ variant }) =>
    variant === 'faCaret' &&
    `
  width: 6.669px;
    `}
`;

const roles = [
  { label: 'Senior Underwriter', role: USER_ROLE.SR_UNDERWRITER },
  { label: 'Underwriter II', role: USER_ROLE.UNDERWRITER },
  { label: 'Account Executive', role: USER_ROLE.ACCOUNT_EXECUTIVE },
  { label: 'Broker', role: USER_ROLE.BROKER },
  { label: 'Closer', role: USER_ROLE.CLOSER },
  { label: 'Closer Team Lead', role: USER_ROLE.CLOSER_TL },
  { label: 'Trac Support', role: USER_ROLE.TRAC_SUPPORT },
];

export function ImpersonateMenu() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isImpersonating } = useUser();
  const {
    isFetching,
    isError,
    data: { userCanImpersonate },
  } = useBoltStatus();

  function onImpersonate(role) {
    Cookies.set(
      BOLT_IMPERSONATE_COOKIE(obfuscatedLoanIdentifier.obfuscatedLoanRecordId),
      role,
      {
        secure: true,
        sameSite: 'strict',
      },
    );
    reloadPage();
  }

  function unImpersonate() {
    Cookies.remove(
      BOLT_IMPERSONATE_COOKIE(obfuscatedLoanIdentifier.obfuscatedLoanRecordId),
    );
    reloadPage();
  }

  function getImpersonatedRole() {
    const impersonatedRole = Cookies.get(
      BOLT_IMPERSONATE_COOKIE(obfuscatedLoanIdentifier.obfuscatedLoanRecordId),
    );
    if (impersonatedRole) {
      const validateRole = roles.find((role) => role.role === impersonatedRole);
      return validateRole ? validateRole.label : 'Unknown Role';
    } else {
      return 'Error Retrieving Role';
    }
  }

  if (isFetching || isError) {
    return null;
  }

  if (!userCanImpersonate) {
    if (isImpersonating) {
      unImpersonate();
    }
    return null;
  }

  if (isImpersonating) {
    return (
      <Button
        display="inline"
        backgroundColor="white"
        color="blue.600"
        fontWeight="500"
        lineHeight="24px"
        _hover={{
          textDecoration: 'none',
          background: 'white',
          borderRadius: '7px',
          border: 'none',
        }}
        _focus={{
          boxShadow: 'none',
        }}
        _active={{
          textDecoration: 'none',
        }}
        height="unset"
        onClick={unImpersonate}
        aria-label="Stop impersonating"
      >
        {getImpersonatedRole()} View
        <StyledIcon icon={faTimes} />
      </Button>
    );
  }

  return (
    <Menu display="inline" onClose={() => {}}>
      {({ isOpen }) => (
        <>
          <MenuButton
            color="blue.600"
            fontWeight="500"
            m="16px"
            lineHeight="24px"
            display="flex"
            alignItems="center"
          >
            Impersonate
            <StyledIcon
              icon={isOpen ? faCaretUp : faCaretDown}
              variant="faCaret"
            />
          </MenuButton>
          <MenuList zIndex={101}>
            {roles.map(({ role, label }) => (
              <MenuItem key={role} onClick={() => onImpersonate(role)}>
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </>
      )}
    </Menu>
  );
}
